import type { FC, ReactNode } from "react";
import { useEffect, useState } from "react";
import { AuthProvider } from "../contexts/AuthContext";
import { AddressProvider } from "../contexts/AddressContext";
import { ContractProvider } from "../contexts/ContractContext";
import { NotificationProvider } from "../contexts/NotificationContext";
import ErrorBoundary from "./ErrorBoundary";
import { useAuth } from "../contexts/AuthContext";

interface ProviderWrapperProps {
  children: ReactNode;
}

// Loading component for context initialization
const ContextLoading: FC = () => (
  <div className="flex h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
  </div>
);

// Error fallback specific to context errors
const ContextErrorFallback: FC<{ error: Error }> = ({ error }) => (
  <div className="flex h-screen flex-col items-center justify-center p-4">
    <div className="rounded-lg bg-white p-8 shadow-xl dark:bg-gray-800">
      <h1 className="mb-4 text-xl font-bold text-red-600 dark:text-red-500">
        Context Error
      </h1>
      <div className="mb-4 text-gray-600 dark:text-gray-300">
        {error.message}
      </div>
      <button
        onClick={() => window.location.reload()}
        className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
      >
        Retry
      </button>
    </div>
  </div>
);

// Wrapper for Address and Contract providers that depend on auth
const AuthDependentProviders: FC<ProviderWrapperProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!loading) {
      setIsInitialized(true);
    }
  }, [loading]);

  if (loading || !isInitialized) {
    return <ContextLoading />;
  }

  // Only render dependent providers if we have a user
  if (!user) {
    return <>{children}</>;
  }

  return (
    <ErrorBoundary fallback={ContextErrorFallback}>
      <AddressProvider>
        <ErrorBoundary fallback={ContextErrorFallback}>
          <ContractProvider>{children}</ContractProvider>
        </ErrorBoundary>
      </AddressProvider>
    </ErrorBoundary>
  );
};

// Main context provider wrapper
export const ContextProviderWrapper: FC<ProviderWrapperProps> = ({
  children,
}) => {
  return (
    <ErrorBoundary fallback={ContextErrorFallback}>
      <NotificationProvider>
        <AuthProvider>
          <AuthDependentProviders>{children}</AuthDependentProviders>
        </AuthProvider>
      </NotificationProvider>
    </ErrorBoundary>
  );
};
